import { inject, Injectable, signal } from '@angular/core';
import { Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { ConfigService } from '../../core/config.service';
import { finalize, takeUntil } from 'rxjs/operators';
import { getHttpErrorMessage } from '../../shared-components/utils/http-error-message.util';


export interface ToastInfo {
  message: string;
  type: 'success' | 'error';
  id: number;
  persistent?: boolean;
  correlationId?: string;
}

export interface Person {
  id: string;
  name: string;
  relationship: string;
  address: string;
  cityStateZip: string;
  mailToSalutation: string;
}

export interface GeneratedLetterInfo {
  caseId: string;
  networkPath: string;
}

export interface DocumentFile {
  id: string;
  name: string;
  pdfUrl?: string;
  checked: boolean;
}

export interface SearchCriteria {
  caseNo: string;
  streetNo: string;
  direction: string | null;
  streetName: string;
  type: string | null;
  unitNo: string;
  apn: string;
}

export interface CaseResult {
  caseNo: string;
  address: string;
  program: string;
  status: 'Open' | 'Closed' | 'Pending' | '';
  openDate: string;
  closeDate: string;
  inspector: string;
  secondInspector: string;
  caseDesc: string;
  apn: string;
}

export interface CaseApiResponse {
  status: string;
  statusCode: number;
  errorMessage: string | null;
  data: CaseResult[];
  totalRecords: number;   // NEW
  correlationId: string;
}

export interface PersonApiResponse {
  status: string;
  statusCode: number;
  errorMessage: string | null;
  data: any[];
  totalRecords: number;
  correlationId: string;
}

@Injectable({
  providedIn: 'root'
})
export class CaseService {
  // Documents state
  private readonly configService = inject(ConfigService);
  private readonly apiBaseUrl = this.configService.get().APIBaseUrl;

  // Search criteria form state
  readonly searchCriteria = signal<SearchCriteria>({
    caseNo: '',
    streetNo: '',
    direction: null,
    streetName: '',
    type: null,
    unitNo: '',
    apn: ''
  });

  // Search results state
  readonly searchResults = signal<CaseResult[]>([]);
  readonly expandedNoteCases = signal<Set<string>>(new Set());
  readonly isSearched = signal<boolean>(false);
  readonly isSearching = signal<boolean>(false);
  readonly selectedCase = signal<CaseResult | null>(null);
  readonly types = signal<string[]>([]);

  // Toast notifications state
  readonly toast = signal<ToastInfo | null>(null);
  private toastTimeoutId: ReturnType<typeof setTimeout> | null = null;

  // Generated letter details
  readonly generatedLetter = signal<GeneratedLetterInfo | null>(null);

  readonly caseTags = signal<string[]>([]);
  readonly casePersons = signal<Person[]>([]);
  readonly selectedTags = signal<string[]>(['DFX Tag']);
  readonly selectedTemplate = signal<string>('');

  // Configurable top banner text
  readonly bannerText = signal<string>('CES Mail Merge');

  // FIX: Track state
  readonly currentPage = signal<number>(1);
  readonly pageSize = signal<number>(10);
  readonly totalRecords = signal<number>(0);

  // Person pagination state — mirrors currentPage/pageSize/totalRecords used for case search
  readonly personsCurrentPage = signal<number>(1);
  readonly personsPageSize = signal<number>(10);
  readonly personsTotalRecords = signal<number>(0);
  readonly isLoadingPersons = signal<boolean>(false);

  // FIX: Create subjects to control subscription lifetimes
  private readonly searchCancel$ = new Subject<void>();
  private readonly personSearchCancel$ = new Subject<void>();

  constructor(private http: HttpClient) { }

  // Update search criteria field
  updateCriteria(key: keyof SearchCriteria, value: string) {
    this.searchCriteria.update(prev => ({
      ...prev,
      [key]: value
    }));
  }

  performSearch(page: number = 1, size: number = this.pageSize()) {
    const criteria = this.searchCriteria();

    const payload = {
      caseno: criteria.caseNo ?? '',
      streetNum: criteria.streetNo ?? '',
      streetDir: criteria.direction ?? '',
      streetName: criteria.streetName ?? '',
      streetType: criteria.type ?? '',
      unitNum: criteria.unitNo ?? '',
      apn: criteria.apn ?? '',
      page,
      size
    };

    this.currentPage.set(page);
    this.pageSize.set(size);
    this.isSearching.set(true);
    this.expandedNoteCases.set(new Set());

    // FIX: Cancel previous search request
    this.searchCancel$.next();

    this.http
      .post<CaseApiResponse>(`${this.apiBaseUrl}/search/cases`, payload)
      .pipe(
        // FIX: Use takeUntil to unsubscribe when searchCancel$ emits
        takeUntil(this.searchCancel$),
        finalize(() => {
          this.isSearching.set(false);  // ✅ Runs on both success & error
        })
      )
      .subscribe({
        next: (response) => {
          if (!response || typeof response !== 'object') {
            this.searchResults.set([]);
            this.totalRecords.set(0);
            this.selectedCase.set(null);
            this.showToast('The case search returned an invalid response.', 'error');
            return;
          }

          if (response.status !== 'SUCCESS') {
            this.searchResults.set([]);
            this.totalRecords.set(0);
            this.selectedCase.set(null);
            this.showToast(
              response.errorMessage || 'Failed to fetch cases.',
              'error',
              false,
              response.correlationId,
            );
            return;
          }

          const results = (Array.isArray(response.data) ? response.data : [])
            .filter((c): c is CaseResult => typeof c === 'object' && c !== null)
            .map(c => ({
              ...c,
              caseNo: typeof c.caseNo === 'string' ? c.caseNo : '',
              address: typeof c.address === 'string' ? c.address.trim() : '',
              program: typeof c.program === 'string' ? c.program : '',
              status: c.status ?? '',
              openDate: typeof c.openDate === 'string' ? c.openDate : '',
              closeDate: typeof c.closeDate === 'string' ? c.closeDate : '',
              inspector: typeof c.inspector === 'string' ? c.inspector : '',
              secondInspector: typeof c.secondInspector === 'string' ? c.secondInspector : '',
              caseDesc: typeof c.caseDesc === 'string' ? c.caseDesc : '',
              apn: typeof c.apn === 'string' ? c.apn : '',
            }));

          this.searchResults.set(results);
          this.totalRecords.set(response.totalRecords ?? 0);
          this.isSearched.set(true);
          this.selectedCase.set(results.length ? results[0] : null);

          if (results.length) {
            this.showToast('Cases fetched successfully.', 'success');
          } else {
            this.showToast('No data available for the given record.', 'success');
          }
        },
        error: (err) => {
          console.error('Search API failed:', err);
          this.searchResults.set([]);
          this.totalRecords.set(0);
          this.selectedCase.set(null);
          const message = getHttpErrorMessage(err, {
            service: 'case search service',
            operation: 'searching cases',
            fallback: 'Failed to fetch cases. Please try again.',
          });
          this.showToast(
            message,
            'error',
            false,
            err.error?.correlationId,
          );
        }
      });
  }

  // NEW — called by SearchResultsComponent when the table requests a different page/size
  changePage(page: number, size: number) {
    this.performSearch(page, size);
  }

  // Reset criteria and results
  resetSearch() {
    this.searchCancel$.next();
    this.searchCriteria.set({
      caseNo: '',
      streetNo: '',
      direction: null,
      streetName: '',
      type: null,
      unitNo: '',
      apn: ''
    });
    this.searchResults.set([]);
    this.expandedNoteCases.set(new Set());
    this.isSearched.set(false);
    this.isSearching.set(false);
    this.selectedCase.set(null);
    this.clearToast();
    this.currentPage.set(1);
    this.totalRecords.set(0);
  }

  toggleTag(tag: string) {
    this.selectedTags.update(tags => {
      if (tags.includes(tag)) {
        return tags.filter(t => t !== tag);
      } else {
        return [...tags, tag];
      }
    });
  }

  // Add a new custom tag
  addNewTag(tag: string) {
    const cleanTag = tag.trim();
    if (cleanTag && !this.caseTags().includes(cleanTag)) {
      this.caseTags.update(tags => [...tags, cleanTag]);
      this.selectedTags.update(tags => [...tags, cleanTag]);
    }
  }

  // Set selected template
  setTemplate(template: string) {
    this.selectedTemplate.set(template);
  }

  showToast(
    message: string,
    type: 'success' | 'error',
    persistent = false,
    correlationId?: string,
  ) {
    // FIX: Clear previous timeout
    if (this.toastTimeoutId) {
      clearTimeout(this.toastTimeoutId);
      this.toastTimeoutId = null;
    }

    const id = Date.now();
    this.toast.set({
      message,
      type,
      id,
      persistent,
      ...(correlationId ? { correlationId } : {}),
    });

    if (!persistent) {
      this.toastTimeoutId = setTimeout(() => {
        if (this.toast()?.id === id) {
          this.toast.set(null);
        }
        this.toastTimeoutId = null;
      }, 5000);
    }
  }

  clearToast() {
    if (this.toastTimeoutId) {
      clearTimeout(this.toastTimeoutId);
      this.toastTimeoutId = null;
    }
    this.toast.set(null);
  }

  getCasePerson(caseNo: string, page: number = 1, size: number = this.personsPageSize()) {
    console.trace('getCasePerson called', { caseNo, page, size });
    const payload = { caseNo, page, size };

    this.personsCurrentPage.set(page);
    this.personsPageSize.set(size);
    this.isLoadingPersons.set(true);

    // FIX: Cancel previous person search request
    this.personSearchCancel$.next();

    this.http
      .post<PersonApiResponse>(`${this.apiBaseUrl}/search/caseperson`, payload)
      .pipe(
        // FIX: Use takeUntil to unsubscribe when personSearchCancel$ emits
        takeUntil(this.personSearchCancel$),
        finalize(() => this.isLoadingPersons.set(false))
      )
      .subscribe({
        next: (response) => {
          if (!response || typeof response !== 'object') {
            this.casePersons.set([]);
            this.personsTotalRecords.set(0);
            this.showToast('The person search returned an invalid response.', 'error');
            return;
          }

          if (response.status !== 'SUCCESS') {
            this.casePersons.set([]);
            this.personsTotalRecords.set(0);
            this.showToast(
              response.errorMessage || 'Failed to fetch persons.',
              'error',
              false,
              response.correlationId,
            );
            return;
          }

          const persons: Person[] = (Array.isArray(response.data) ? response.data : [])
            .filter((person: unknown): person is Record<string, unknown> =>
              typeof person === 'object' && person !== null,
            )
            .map((person, idx) => {
              const name = typeof person['name'] === 'string' ? person['name'] : '';
              return {
                id: `${name}-${idx}`,
                name,
                relationship: typeof person['relationship'] === 'string' ? person['relationship'] : '',
                address: typeof person['address'] === 'string' ? person['address'] : '',
                cityStateZip: typeof person['cityStateZip'] === 'string' ? person['cityStateZip'] : '',
                mailToSalutation: typeof person['mailToSalutation'] === 'string'
                  ? person['mailToSalutation']
                  : '',
              };
            });
          this.casePersons.set(persons);
          this.personsTotalRecords.set(response.totalRecords ?? 0);
        },
        error: (err) => {
          console.error('Person search API failed:', err);
          this.casePersons.set([]);
          this.personsTotalRecords.set(0);
          this.showToast(
            err.error?.errorMessage || 'Failed to fetch persons.',
            'error',
            false,
            err.error?.correlationId,
          );
        }
      });
  }

  // Called by PersonModal when the table's page or page-size changes
  changePersonPage(caseNo: string, page: number, size: number) {
    this.getCasePerson(caseNo, page, size);
  }

  cleanup(): void {
    // Cancel all pending HTTP requests
    this.searchCancel$.next();
    this.searchCancel$.complete();

    this.personSearchCancel$.next();
    this.personSearchCancel$.complete();

    // Clear pending toast timeout
    if (this.toastTimeoutId) {
      clearTimeout(this.toastTimeoutId);
      this.toastTimeoutId = null;
    }

    this.resetSearch();
    this.clearToast();
  }
}